import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
//stores
import { useUserStore } from 'stores/userStore';
import { useCompanyStore } from 'stores/companyStore';
//components
import TitleWithLine from 'components/titleWithLine';
import CustomTab from 'components/Tab';
import AccountData from './accountData';
import BenefitedBookStore from './benefitedBookStore';
import Addresses from './addresses';
//styles
import './profile.scss';

const Profile = () => {
  const userInfo = useUserStore();
  const { name, is_single_company } = useCompanyStore();
  const { id, benefitedBookstore, status: userStatus, fetchUserInfo, updateUserInfo } = userInfo;
  const { t } = useTranslation(['register', 'header']);
  const [disableBtnSave, setDisableBtnSave] = useState(false);
  const [errorSave, setErrorSave] = useState('');
  const [successSave, setSuccessSave] = useState(false);
  const [successAddressSave, setSuccessAddressSave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formInfo, setFormInfo] = useState({
    cif: '',
    name: '',
    last_name: '',
    email: '',
    birth_date: '',
    direccion_facturacion: {},
    direccion_envio: {},
    benefited_bookstore: benefitedBookstore || '',
  });
  const [updateData, setUpdateData] = useState({
    first_name: '',
    last_name: '',
    date_birth: '',
    email: '',
    dni: '',
    phone_number: '',
  });

  const defaultAddress = useMemo(
    () => ({
      address: '',
      country: null,
      email: '',
      first_name: '',
      last_name: '',
      locality: '',
      phone: '',
      postal_code: '',
      province: null,
    }),
    [],
  );

  const [updateDataAddress, setUpdateDataAddress] = useState({
    billing_address: defaultAddress,
  });

  const [updateDataShipping, setUpdateDataShipping] = useState({
    shipping_address: defaultAddress,
  });

  const setFormData = useCallback(
    (data) => {
      setUpdateData({
        first_name: data?.firstName,
        last_name: data?.lastName,
        date_birth: data.dateBirth || '',
        email: data.email,
        dni: data.dni || '',
        phone_number: data.phoneNumber || '',
      });
      
      // Prellenar dirección de facturación con datos del usuario si está vacía o no tiene estos campos
      const billingAddress = data?.billingAddress || {};
      // Verificar si country y province tienen valores válidos, si no, usar null
      const billingCountry = billingAddress?.country && billingAddress?.country?.value ? billingAddress.country : null;
      const billingProvince = billingAddress?.province && billingAddress?.province?.value ? billingAddress.province : null;
      
      const billingAddressWithUserData = {
        ...defaultAddress,
        ...billingAddress,
        // Prellenar con datos del usuario solo si la dirección no tiene estos campos
        first_name: billingAddress?.first_name || data?.firstName || '',
        last_name: billingAddress?.last_name || data?.lastName || '',
        email: billingAddress?.email || data?.email || '',
        phone: billingAddress?.phone || billingAddress?.phone_number || data?.phoneNumber || '',
        country: billingCountry,
        province: billingProvince,
      };
      
      // Prellenar dirección de envío con datos del usuario si está vacía o no tiene estos campos
      const shippingAddress = data?.shippingAddress || {};
      // Verificar si country y province tienen valores válidos, si no, usar null
      const shippingCountry = shippingAddress?.country && shippingAddress?.country?.value ? shippingAddress.country : null;
      const shippingProvince = shippingAddress?.province && shippingAddress?.province?.value ? shippingAddress.province : null;
      
      const shippingAddressWithUserData = {
        ...defaultAddress,
        ...shippingAddress,
        // Prellenar con datos del usuario solo si la dirección no tiene estos campos
        first_name: shippingAddress?.first_name || data?.firstName || '',
        last_name: shippingAddress?.last_name || data?.lastName || '',
        email: shippingAddress?.email || data?.email || '',
        phone: shippingAddress?.phone || shippingAddress?.phone_number || data?.phoneNumber || '',
        country: shippingCountry,
        province: shippingProvince,
      };
      
      setUpdateDataAddress({
        billing_address: billingAddressWithUserData,
      });
      setUpdateDataShipping({
        shipping_address: shippingAddressWithUserData,
      });
      setFormInfo({
        cif: data?.dni || '',
        name: data?.firstName || '',
        last_name: data?.lastName || '',
        email: data.email || '',
        birth_date: data.dateBirth || '',
        direccion_facturacion: data.billingAddress || {},
        direccion_envio: data.shippingAddress || {},
        benefited_bookstore: data?.benefitedBookstore,
      });
    },
    [defaultAddress],
  );

  useEffect(() => {
    if (userStatus === 'idle') {
      fetchUserInfo();
    }
    if (userStatus === 'succeeded') {
      setFormData(userInfo);
    }
  }, [userStatus, userInfo, fetchUserInfo, setFormData]);

  const handleSaveAddress = async (addressType, addressData) => {
    try {
      const provinceValue =
        addressData?.province?.label ??
        addressData?.province?.value ??
        (typeof addressData?.province === 'string' ? addressData.province : '');
      const customData = {
        ...addressData,
        province: provinceValue,
      };
      const addressInfo = {
        [addressType]: customData,
      };
      await updateUserInfo({ data: addressInfo, id });
      setSuccessSave(false);
      setSuccessAddressSave(true);
      fetchUserInfo();
    } catch (error) {
      console.error('Error:', error);
      setErrorSave('Error message');
    } finally {
      setDisableBtnSave(false);
    }
  };

  const handleSaveDF = async () => {
    setDisableBtnSave(true);
    setErrorSave('');
    setSuccessAddressSave(false);
    await handleSaveAddress('billing_address', updateDataAddress?.billing_address);
  };

  const handleSaveDE = async () => {
    setDisableBtnSave(true);
    setErrorSave('');
    setSuccessAddressSave(false);
    await handleSaveAddress('shipping_address', updateDataShipping?.shipping_address);
  };

  useEffect(() => {
    if (userStatus === 'idle') {
      fetchUserInfo();
    }
    window.document.title = `${t('myaccount', { ns: 'header' })} | ` + name;
    goUp();
  }, [userStatus, fetchUserInfo, name, t]);

  const recoverUserInfo = useCallback(async () => {
    if (userStatus === 'idle') {
      fetchUserInfo();
    }
  }, [userStatus, fetchUserInfo]);

  useEffect(() => {
    recoverUserInfo();
  }, [userStatus, fetchUserInfo, recoverUserInfo]);

  useEffect(() => {
    let time = null;
    let time2 = null;
    if (successSave) {
      time = setTimeout(() => setSuccessSave(false), 3000);
    }
    if (successAddressSave) {
      time2 = setTimeout(() => setSuccessAddressSave(false), 3000);
    }
    return () => {
      clearTimeout(time);
      clearTimeout(time2);
    };
  }, [successSave, successAddressSave]);

  const handleChangeSave = async () => {
    setDisableBtnSave(true);
    setErrorSave('');
    setSuccessAddressSave(false);
    setLoading(true);
    try {
      await updateUserInfo({ data: formInfo, id });
      fetchUserInfo();
      setDisableBtnSave(false);
      setSuccessSave(true);
    } catch (e) {
      console.log(e);
      setErrorSave(t('errormessage', { ns: 'header' }));
      setDisableBtnSave(false);
      setSuccessSave(false);
    }
    setLoading(false);
  };

  const goUp = () => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="container-fluid mainDiv margin-progile">
      <Row>
        <Col md={12}>
          <TitleWithLine text={t('myaccount', { ns: 'header' })} />
        </Col>
      </Row>
      <Row className="tabs-row tab-profile">
        <CustomTab
          customClass="custom-tab p-3 p-lg-0"
          tabs={[
            {
              title: t('accountdata', { ns: 'register' }),
              id: 'accountdata',
              renderContent: () => (
                <AccountData updateData={updateData} setUpdateData={setUpdateData} disableBtnSave={disableBtnSave} />
              ),
            },
            !is_single_company && {
              title: t('benefitedbookstore', { ns: 'register' }),
              id: 'benefitedbookstore',
              renderContent: () => (
                <BenefitedBookStore
                  formInfo={formInfo}
                  setFormInfo={setFormInfo}
                  handleChangeSave={handleChangeSave}
                  errorSave={errorSave}
                  successSave={successSave}
                  loading={loading}
                />
              ),
            },
            {
              title: t('billingaddress', { ns: 'register' }),
              id: 'billingaddress',
              renderContent: () => (
                <Addresses
                  address="billing_address"
                  errorSave={errorSave}
                  successAddressSave={successAddressSave}
                  updateData={updateDataAddress}
                  setUpdateData={setUpdateDataAddress}
                  handleSave={handleSaveDF}
                />
              ),
            },
            {
              title: t('shippingaddress', { ns: 'register' }),
              id: 'shippingaddress',
              renderContent: () => (
                <Addresses
                  address="shipping_address"
                  errorSave={errorSave}
                  successAddressSave={successAddressSave}
                  updateData={updateDataShipping}
                  setUpdateData={setUpdateDataShipping}
                  handleSave={handleSaveDE}
                />
              ),
            },
          ].filter(Boolean)}
        />
      </Row>
    </div>
  );
};

export default Profile;
